export const SOCIAL_EMBED_PROVIDER = {
  TikTok: 'tiktok',
  Instagram: 'instagram',
} as const;

export type SocialEmbedProvider = typeof SOCIAL_EMBED_PROVIDER[keyof typeof SOCIAL_EMBED_PROVIDER];

export interface SocialEmbed {
  provider: SocialEmbedProvider;
  id: string;
}

export const socialEmbedProviderLabel = (provider: SocialEmbedProvider): string =>
  provider === SOCIAL_EMBED_PROVIDER.TikTok ? 'TikTok' : 'Instagram';

const TIKTOK_HOST = /^(?:www\.|m\.)?tiktok\.com$/i;
const TIKTOK_VIDEO_PATH = /^\/@[\w.-]+\/video\/(\d+)\/?$/;
const TIKTOK_ID = /^\d+$/;

const INSTAGRAM_HOST = /^(?:www\.)?instagram\.com$/i;
const INSTAGRAM_POST_PATH = /^\/(?:[\w.]+\/)?(?:p|reel|reels|tv)\/([\w-]+)\/?$/;
const INSTAGRAM_ID = /^[\w-]+$/;

const SOCIAL_EMBED_PROVIDERS = Object.values(SOCIAL_EMBED_PROVIDER) as string[];

const toPostUrl = (input: string): URL | null => {
  try {
    const url = new URL(input.trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
  }
  catch {
    return null;
  }
};

export const parseSocialEmbedUrl = (input: string): SocialEmbed | null => {
  const url = toPostUrl(input);
  if (!url) {
    return null;
  }

  if (TIKTOK_HOST.test(url.hostname)) {
    const id = url.pathname.match(TIKTOK_VIDEO_PATH)?.[1];
    return id ? { provider: SOCIAL_EMBED_PROVIDER.TikTok, id } : null;
  }

  if (INSTAGRAM_HOST.test(url.hostname)) {
    const id = url.pathname.match(INSTAGRAM_POST_PATH)?.[1];
    return id ? { provider: SOCIAL_EMBED_PROVIDER.Instagram, id } : null;
  }

  return null;
};

/**
 * The post's public link with only its host and path kept, so share-tracking ids (`igsh`, `_t`),
 * any other query or fragment, and credentials never reach the published content. Null for a
 * link that isn't an embeddable post.
 */
export const canonicalSocialEmbedUrl = (input: string): string | null => {
  const embed = parseSocialEmbedUrl(input);
  const url = toPostUrl(input);
  if (!embed || !url) {
    return null;
  }

  const host = embed.provider === SOCIAL_EMBED_PROVIDER.TikTok ? 'www.tiktok.com' : 'www.instagram.com';
  return `https://${host}${url.pathname}`;
};

/**
 * Narrows stored node attrs to an embed, so a hand-edited or corrupted id can never reach
 * an iframe src: only the fixed provider templates below are ever framed.
 */
export const toSocialEmbed = ({ provider, id }: { provider?: string | null; id?: string | null }): SocialEmbed | null => {
  if (typeof provider !== 'string' || typeof id !== 'string' || !SOCIAL_EMBED_PROVIDERS.includes(provider)) {
    return null;
  }

  const pattern = provider === SOCIAL_EMBED_PROVIDER.TikTok ? TIKTOK_ID : INSTAGRAM_ID;
  return pattern.test(id) ? { provider: provider as SocialEmbedProvider, id } : null;
};

export const socialEmbedSrc = ({ provider, id }: SocialEmbed): string =>
  provider === SOCIAL_EMBED_PROVIDER.TikTok
    ? `https://www.tiktok.com/player/v1/${id}`
    : `https://www.instagram.com/p/${id}/embed/`;
